import {
  FC,
  useState,
  useCallback,
  ChangeEvent,
  useMemo,
  useEffect
} from "react";
import {
  Box,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  useMediaQuery,
  Link,
  ChakraProvider,
  ColorModeScript,
  Select,
  LightMode
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUser } from "@fortawesome/free-regular-svg-icons";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import { PrimaryButton } from "../../lib/components/PrimaryButton";
import { useHubspotForm } from "../../api/hubspot-form";
import { theme } from "../../lib/theme";
import {
  countries,
  DEFAULT_COUNTRY_CODE,
  getCountryByCode
} from "../../utils/countries";

const key = "@Aristo/landing-contact";

const HUBSPOT_PORTAL_ID = "47497438";
const HUBSPOT_FORM_ID = "b9c44098-1e4b-4b8d-8be2-922da7eeb6d2";
const HUBSPOT_MOMENT_FIELD = "aristo_momento_e_perfil__cadastro_trial";

const momentOptions = [
  "Ensino médio / pré-vestibular (interessado em Medicina)",
  "Estudante de Medicina (1º ao 4º ano)",
  "Estudante de Medicina (internato)",
  "Médico(a) recém-formado(a)",
  "Médico(a) residente",
  "Médico(a) especialista",
  "Médico(a) em preparação para residência",
  "Médico(a) em preparação para prova de título",
  "Médico(a) em transição de carreira",
  "Outro"
];

const BR_COUNTRY_CODE = "BR";

// DDD 11-99 + celular (9 + 8 dígitos) ou fixo (2-8 + 7 dígitos).
const BR_PHONE_REGEX = /^[1-9]{2}(?:9\d{8}|[2-8]\d{7})$/;

const MAX_BR_DIGITS = 11;
const MAX_INTL_DIGITS = 15;

/**
 * Aplica a máscara progressivamente: cada separador só entra quando existe
 * dígito depois dele. Assim o backspace nunca fica preso num "-" ou ")"
 * que a própria máscara reinseriria.
 */
function formatPhone(value: string, countryCode: string): string {
  if (countryCode !== BR_COUNTRY_CODE) {
    return value.replace(/\D/g, "").slice(0, MAX_INTL_DIGITS);
  }

  const digits = value.replace(/\D/g, "").slice(0, MAX_BR_DIGITS);

  if (!digits) {
    return "";
  }

  if (digits.length <= 2) {
    return `(${digits}`;
  }

  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);

  if (rest.length <= 4) {
    return `(${ddd}) ${rest}`;
  }

  if (rest.length <= 8) {
    return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }

  return `(${ddd}) ${rest.slice(0, 1)} ${rest.slice(1, 5)}-${rest.slice(5)}`;
}

const appTheme = theme(
  {
    initialColorMode: "light",
    useSystemColorMode: false
  },
  {
    components: {
      Input: {
        baseStyle: {
          field: {
            _placeholder: { color: "gray.400" },
            borderColor: "blue.500",
            _hover: { borderColor: "blue.500" },
            _focus: { borderColor: "blue.500" }
          }
        }
      },
      Select: {
        baseStyle: {
          field: {
            borderColor: "blue.500",
            _hover: { borderColor: "blue.500" },
            _focus: { borderColor: "blue.500" }
          }
        }
      }
    }
  }
);

const LandingLogin: FC = () => {
  const history = useHistory();

  const [showBgImage] = useMediaQuery("(min-width: 1260px)");
  const { onSubmitForm } = useHubspotForm();

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [phone, setPhone] = useState("");
  const [moment, setMoment] = useState("");
  const [loading, setLoading] = useState(false);

  const isNameValid = useMemo(() => {
    return name.trim().length >= 2;
  }, [name]);

  const phoneDigits = useMemo(() => phone.replace(/\D/g, ""), [phone]);

  const isPhoneValid = useMemo(() => {
    if (countryCode === BR_COUNTRY_CODE) {
      return BR_PHONE_REGEX.test(phoneDigits);
    }

    return phoneDigits.length >= 8 && phoneDigits.length <= 15;
  }, [countryCode, phoneDigits]);

  const showPhoneError = useMemo(() => {
    return phoneDigits.length > 0 && !isPhoneValid;
  }, [isPhoneValid, phoneDigits.length]);

  const isEmailValid = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, [email]);

  const disabled = useMemo(() => {
    return !isNameValid || !isPhoneValid || !isEmailValid;
  }, [isEmailValid, isNameValid, isPhoneValid]);

  const handlePhone = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPhone(formatPhone(e.target.value, countryCode));
    },
    [countryCode]
  );

  const handleCountryCode = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    setCountryCode(code);
    setPhone(current => formatPhone(current, code));
  }, []);

  const fullPhoneNumber = useMemo(() => {
    const { dialCode } = getCountryByCode(countryCode);
    return `${dialCode}${phoneDigits}`;
  }, [countryCode, phoneDigits]);

  const onClick = useCallback(async () => {
    if (disabled) {
      return;
    }

    setLoading(true);
    try {
      await onSubmitForm({
        portalId: HUBSPOT_PORTAL_ID,
        formId: HUBSPOT_FORM_ID,
        fields: [
          { name: "email", value: email },
          { name: "firstname", value: name.trim() },
          { name: "lastname", value: surname.trim() },
          { name: "phone", value: fullPhoneNumber },
          ...(moment ? [{ name: HUBSPOT_MOMENT_FIELD, value: moment }] : [])
        ]
      });
      localStorage.setItem(
        key,
        JSON.stringify({
          name,
          surname,
          email,
          phone: fullPhoneNumber,
          moment
        })
      );
      history.push("/landing/recursos");
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [
    disabled,
    email,
    fullPhoneNumber,
    history,
    moment,
    name,
    onSubmitForm,
    surname
  ]);

  useEffect(() => {
    const localContact = localStorage.getItem(key);

    if (localContact) {
      history.push("/landing/recursos");
    }
  }, [history]);

  return (
    <ChakraProvider theme={appTheme}>
      <ColorModeScript initialColorMode={appTheme.config.initialColorMode} />
      <LightMode>
        <div
          style={{
            backgroundImage: showBgImage
              ? "url(/images/landing/cover.png)"
              : "",
            backgroundPosition: "left",
            backgroundRepeat: "no-repeat",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#191C20",
            backgroundColor: "#fff"
          }}
        >
          <Box w="470px" p={4}>
            <Text fontSize={24} mb={1} lineHeight={1.3}>
              Cadastre-se para acessar os recursos
            </Text>
            <Text fontSize={14} mb={6}>
              Insira seu nome, e-mail e telefone para acessar gratuitamente os
              recursos das questões da sua instituição.
            </Text>
            <Flex mb={4} gridGap={3} direction={{ base: "column", sm: "row" }}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FontAwesomeIcon icon={faUser} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Nome*"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </InputGroup>
              <Input
                placeholder="Sobrenome"
                value={surname}
                onChange={e => setSurname(e.target.value)}
              />
            </Flex>
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faEnvelope} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Email*"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </InputGroup>
            <Box mb={4}>
              <Flex gridGap={3}>
                <Select
                  w="130px"
                  flexShrink={0}
                  value={countryCode}
                  onChange={handleCountryCode}
                  aria-label="País"
                >
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {`${country.flag} ${country.dialCode}`}
                    </option>
                  ))}
                </Select>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FontAwesomeIcon icon={faPhone} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder={
                      countryCode === BR_COUNTRY_CODE
                        ? "(00) 00000-0000"
                        : "Telefone*"
                    }
                    value={phone}
                    onChange={handlePhone}
                    isInvalid={showPhoneError}
                    maxLength={16}
                  />
                </InputGroup>
              </Flex>
              {showPhoneError && (
                <Text fontSize={12} color="red.500" mt={1}>
                  Digite um telefone válido com DDD.
                </Text>
              )}
            </Box>
            <Select
              mb={6}
              value={moment}
              onChange={e => setMoment(e.target.value)}
              placeholder="Qual seu momento atual?"
              color={moment ? undefined : "gray.400"}
            >
              {momentOptions.map(option => (
                <option
                  key={option}
                  value={option}
                  style={{ color: "#191C20" }}
                >
                  {option}
                </option>
              ))}
            </Select>
            <PrimaryButton
              size="lg"
              borderRadius={8}
              w="100%"
              mb={4}
              onClick={onClick}
              disabled={loading || disabled}
            >
              Acessar recursos
            </PrimaryButton>
            <Text fontSize={14} color="#75777E">
              Nós respeitamos sua privacidade. Seus dados serão usados apenas
              para o acesso aos recursos e não serão compartilhados com
              terceiros.
            </Text>
            <Text fontSize={14} color="#75777E" mb={4}>
              Ao preencher este formulário e acessar os recursos, você concorda
              com nossa Política de Privacidade e Termos de Uso.
            </Text>
            <Flex>
              <Link
                href="https://pages.aristo.com.br/politicas-de-privacidade/"
                isExternal
                mr={4}
              >
                <Text fontSize={14} fontWeight={500}>
                  Política de Privacidade
                </Text>
              </Link>
              <Link
                href="https://pages.aristo.com.br/termos-de-uso/"
                isExternal
              >
                <Text fontSize={14} fontWeight={500}>
                  Termos de uso
                </Text>
              </Link>
            </Flex>
          </Box>
        </div>
      </LightMode>
    </ChakraProvider>
  );
};

export default LandingLogin;
